/**
 * Passerelle unique vers le backend.
 *
 * - Si VITE_API_BASE_URL est défini, l'application parle au backend autonome
 *   (Express + PostgreSQL) via HTTP.
 * - Sinon, elle continue d'utiliser l'infrastructure actuelle : la bascule se
 *   fait donc avec une seule variable d'environnement, et un retour arrière
 *   immédiat est possible sans toucher au code.
 */
import { supabase } from './supabase';

export interface ApiResponse {
  ok: boolean;
  code?: string;
  message?: string;
  [key: string]: unknown;
}

export type BackendRoute = 'auth' | 'admin' | 'transfer';

/** URL du backend autonome (sans slash final). Vide = infrastructure actuelle. */
export const apiBaseUrl = String(import.meta.env.VITE_API_BASE_URL ?? '')
  .trim()
  .replace(/\/+$/, '');

export const usingRemoteBackend = apiBaseUrl.length > 0;

const LEGACY_FUNCTIONS: Record<BackendRoute, string> = {
  auth: 'mx-auth',
  admin: 'mx-admin',
  transfer: 'mx-transfer',
};

async function callLegacy(route: BackendRoute, payload: Record<string, unknown>): Promise<ApiResponse> {
  try {
    const { data, error } = await supabase.functions.invoke(LEGACY_FUNCTIONS[route], { body: payload });
    if (error) return { ok: false, code: 'NETWORK_ERROR' };
    return (data ?? { ok: false, code: 'SERVER_ERROR' }) as ApiResponse;
  } catch {
    return { ok: false, code: 'NETWORK_ERROR' };
  }
}

async function callRemote(route: BackendRoute, payload: Record<string, unknown>): Promise<ApiResponse> {
  try {
    const response = await fetch(`${apiBaseUrl}/${route}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const text = await response.text();
    let parsed: unknown = null;
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }

    if (!parsed || typeof parsed !== 'object') {
      return { ok: false, code: 'SERVER_ERROR', message: text.slice(0, 300) };
    }

    return parsed as ApiResponse;
  } catch {
    return { ok: false, code: 'NETWORK_ERROR' };
  }
}

export function callBackend(route: BackendRoute, payload: Record<string, unknown>): Promise<ApiResponse> {
  return usingRemoteBackend ? callRemote(route, payload) : callLegacy(route, payload);
}